import { createStyleTag } from '../util/create-style-tag';
import ModalCss from './index.css';
import SpinnerCSS from './spinner.css';

/**
 * Type representing different options for creating a Modal
 */
type ModalOptions = {
  // Initial content to populate Modal with. Can be set later using Modal.setContent().
  // Defaults to an empty <div> element.
  content: HTMLElement;
  // Callback function to call when showModal has been called
  onShow?: () => void;
  // Callback function to call when hideModal has been called
  onHide?: () => void;
};

export default class Modal {
  public shadowHost: HTMLElement;
  public shadowRoot: ShadowRoot;
  protected _modalContainer: HTMLElement;
  protected _modalEl: HTMLElement;
  protected _closeBtn: HTMLElement;
  onShow?: () => void;
  onHide?: () => void;

  /**
   * Set up a shadow root for the modal and create the HTML structure for it.
   * @param options: Options for initializing the Modal. SEE type ModalOptions for more details.
   */
  constructor(options: Partial<ModalOptions> = {}) {
    this.onShow = options.onShow;
    this.onHide = options.onHide;

    // Shadow host: The regular DOM node that the shadow DOM is attached to.
    this.shadowHost = document.createElement('div');
    this.shadowHost.style.display = 'none'; // start modal hidden
    this.shadowRoot = this.shadowHost.attachShadow({ mode: 'open' });

    // Create Elements
    this._modalEl = Modal.renderModalElement(); // #ol-modal
    this._modalContainer = Modal.renderContainerElement(); // #ol-modal-container
    this._closeBtn = Modal.renderCloseButtonElement(); // #ol-closeModal
    this.shadowRoot.appendChild(this._modalEl);
    this._modalEl.appendChild(this._modalContainer);
    // todo add spinner to ol-modal-container ROGUE-297
    this._modalEl.appendChild(this._closeBtn);

    // add event listeners to elements
    this._closeBtn.addEventListener('click', this.hideModal.bind(this));

    // add styling for modal
    const style = document.createElement('style');
    style.innerHTML = ModalCss.toString();
    this.shadowRoot.appendChild(style);
  }

  /**
   * Remove previous modal contents and insert the specified HTML Node Tree
   * @param content: HTML Element to use as modal content
   */
  setContent(content: HTMLElement) {
    if (!document.contains(this.shadowHost)) {
      document.body.appendChild(this.shadowHost);
    }
    this._modalContainer.innerHTML = '';
    this._modalContainer.appendChild(content);
  }

  /**
   * Shows the modal by un-setting 'display: none' on the Shadow Root Element
   *
   * @param {HTMLElement} [content]: Optional new content to display in the modal
   */
  showModal(content?: HTMLElement) {
    // update content, if necessary
    if (content) {
      this.setContent(content);
    }

    this.setParentPageStyling();
    this.shadowHost.style.display = '';

    if (this.onShow) {
      this.onShow();
    }
  }

  /**
   * Hides the modal by setting display: none
   */
  hideModal(_e?: Event) {
    this.shadowHost.style.display = 'none';
    this.unsetParentPageStyling();

    if (this.onHide) {
      this.onHide();
    }
  }

  /**
   * Set some attributes about the parent page style. This prevents scrolling on the main webpage while
   * the modal is shown, and reduces the z-index of headers, so they don't display over the modal.
   * @protected
   */
  protected setParentPageStyling() {
    // Store current scroll position
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    document.body.setAttribute('ol-scroll-position', scrollY.toString());

    if (document.body.style.overflow) {
      // Store the previous body overflow style as an additional attribute for resetting later (if it's set).
      document.body.setAttribute('ol-style-overflow', document.body.style.overflow);
    }

    // Create an overlay div to prevent scrolling without affecting rendering
    const overlay = document.createElement('div');
    overlay.id = 'ol-scroll-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.zIndex = '-1'; // Behind content
    document.body.appendChild(overlay);

    // Simply prevent scrolling without changing the positioning context
    document.body.style.overflow = 'hidden';

    // Add a small delay to ensure we maintain scroll position
    setTimeout(() => {
      window.scrollTo(0, scrollY);
    }, 0);

    // remove z-index from the partner's header element.
    // todo: this could be a more dynamic check for any element with a higher z-index (ROGUE-299). This specific check
    //   for a header element exists for legacy reasons - it was present in the previous widget code. Use case unknown.
    const partner_header = window.document.getElementById('header');
    if (partner_header !== null) {
      partner_header.setAttribute('ol-style-z-index', partner_header.style.zIndex);
      partner_header.style.zIndex = 'unset!important;';
    }
  }

  /**
   * Restores the original parent page styling once the modal is hidden.
   * @protected
   */
  protected unsetParentPageStyling() {
    // Remove the overlay div if it exists
    const overlay = document.getElementById('ol-scroll-overlay');
    if (overlay) {
      document.body.removeChild(overlay);
    }

    // reset body overflow style
    const docOverflow = document.body.getAttribute('ol-style-overflow');
    if (docOverflow) {
      document.body.style.overflow = docOverflow;
      document.body.removeAttribute('ol-style-overflow');
    } else {
      document.body.style.overflow = '';
    }

    // Restore scroll position
    const scrollY = parseInt(document.body.getAttribute('ol-scroll-position') || '0', 10);
    window.scrollTo(0, scrollY);
    document.body.removeAttribute('ol-scroll-position');

    // reset z-index on the partner's header element.
    const partner_header = window.document.getElementById('header');
    if (partner_header !== null) {
      const zIndex = partner_header.getAttribute('ol-style-z-index');
      if (zIndex) partner_header.style.zIndex = zIndex;
    }
  }

  /**
   * Create the HTML Node Tree for the Close button element.
   * @protected
   */
  protected static renderCloseButtonElement() {
    const closeModalEl = window.document.createElement('button');
    closeModalEl.setAttribute('aria-label', 'Close');
    closeModalEl.id = 'ol-closeModal';
    closeModalEl.innerHTML = `
      <svg class="ol-close-modal-icon" xmlns="http://www.w3.org/2000/svg" fill="#000000" viewBox="0 0 43.979 43.979" role="img">
          <path fill="current" d="M12.366 24.865a1.25 1.25 0 1 0 1.768 1.767l7.718-7.717a.21.21 0 0 1 .295 0l7.717 7.719a1.25 1.25 0 0 0 1.769-1.768l-7.717-7.72a.21.21 0 0 1 0-.295l7.718-7.717a1.25 1.25 0 1 0-1.77-1.767l-7.717 7.716a.21.21 0 0 1-.295 0L14.134 7.367a1.25 1.25 0 0 0-1.768 1.767l7.717 7.717a.21.21 0 0 1 0 .295z"></path>
      </svg>`;

    return closeModalEl;
  }

  /**
   * Create the HTML Node Tree for the Outer Modal element.
   * @protected
   */
  protected static renderModalElement() {
    const modalEl = window.document.createElement('div');
    modalEl.id = 'ol-modal';

    return modalEl;
  }

  /**
   * Create the HTML Node Tree for the Inner Modal content container element.
   * @protected
   */
  protected static renderContainerElement() {
    const containerEl = document.createElement('div');
    containerEl.setAttribute('role', 'dialog');
    containerEl.id = 'ol-modal-container';

    return containerEl;
  }

  /**
   * Create the spinner element to display on load
   *
   */
  static renderSpinnerElement() {
    const container = document.createElement('div');
    const spinnerSize = 66;
    const spinnerRadius = 28;
    const spinnerColors = ['#ced4da', '#6c757d', '#2366b1', '#5fb49c'];
    const styleElement = createStyleTag(SpinnerCSS.toString(), 'spinner-css', {
      spinnerSize: `${spinnerSize}px`,
      spinnerRadius: `${spinnerRadius}px`,
      pi: '3.14159',
      spinnerCircumference: 'calc(2 * var(--pi) * var(--spinnerRadius))'
    });
    container.appendChild(styleElement);
    container.style.paddingTop = '100px';
    const fancySpinner = document.createElement('div');
    fancySpinner.classList.add('fancySpinner');
    let fancySpinnerHTML = '';
    spinnerColors.forEach((spinnerColor, index) => {
      fancySpinnerHTML =
        fancySpinnerHTML +
        `
      <svg
      class="stroke"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 66 66"
  >
      <circle
              style="stroke: ${spinnerColor}; animation: strokeAnimation 3s ease calc(.2s * ${index}) infinite"
              r="${spinnerRadius}px" cx="33px" cy="33px"
      ></circle>
  </svg>
`;
    });
    fancySpinner.innerHTML = fancySpinnerHTML;
    container.appendChild(fancySpinner);
    return container;
  }
  /* Create the iFrame HTML Element for the OL Prequal Form.
   * @param iframeSrc
   */
  static renderIframeElement(iframeSrc: string): HTMLIFrameElement {
    const iframeEl = window.document.createElement('iframe');
    iframeEl.referrerPolicy = 'origin-when-cross-origin'; // this allows us to send the referrer to the UX in local
    iframeEl.style.width = iframeEl.style.height = '100%';
    iframeEl.style.border = 'none';
    iframeEl.id = 'ol-iframe';
    iframeEl.src = iframeSrc;

    return iframeEl;
  }
}
